/**
 * Copyright (c) 2018-2019 Aleksey Melnikov, True Directive Company.
 * @link https://truedirective.com/
 * @license MIT
*/
import { Directive, ElementRef, Input, QueryList, EventEmitter, Output,
    Renderer2, Injector,
    OnDestroy, AfterContentInit, DoCheck, OnChanges,
    ComponentFactoryResolver, ApplicationRef,
    SimpleChange } from '@angular/core';

// Base
import { GridLayout, GridLayoutSelection } from '@true-directive/base';
import { ColumnType, RenderMode, Column } from '@true-directive/base';
import { CellPosition, CellHighlighter } from '@true-directive/base';
import { Utils, Strings } from '@true-directive/base';

// Classes
import { RowCell } from './row-cell.class';

// Services
import { GridStateService } from './grid-state.service';

// Editor components
import { EditorTextComponent } from './editors/editor-text.component';
import { EditorSelectTrueComponent } from './editors/editor-select-true.component';
import { EditorNumberComponent } from './editors/editor-number.component';
import { EditorDateComponent } from './editors/editor-date.component';

// Cell components
import { CellHtmlComponent } from './cells/cell-html.component';
import { CellRefComponent } from './cells/cell-ref.component';

/**
 * Grid row directive. It's all about speed.
 */
@Directive({
  selector: '[true-row]'
})
export class RowDirective implements OnDestroy, AfterContentInit, DoCheck, OnChanges {

    /**
     * Row for rendering
     */
    @Input()
    public row: any;

    /**
     * Grid's state
     */
    @Input('true-state')
    public state: GridStateService;

    /**
     * Index in the list of displayed rows
     */
    @Input('true-i')
    public axI: number;

    /**
     * Layout info
     */
    @Input('true-layout')
    layout: GridLayout;

    /**
     * Indicates we're an empty line and we don't need to do anything.
     */
    @Input('true-empty')
    empty = false;

    /**
     * Horizontal scroll position
     */
    @Input('view-port-left')
    viewPortLeft = -1;

    /**
     * Scrollbox width
     */
    @Input('view-port-width')
    viewPortWidth = -1;

    /**
     * Locale
     */
    @Input('true-locale')
    locale: string = '';

    /**
     * Event - clicking on the checkbox - will cause the value to change
     */
    @Output('toggleCheckbox')
    toggleCheckbox = new EventEmitter<any>();

    /**
     * Data of the displayed row
     */
    protected get rowData(): any {
      return this.state.dataSource.rowData(this.row);
    }

    private _left_rendered = -1;
    private _right_rendered = -1;

    /**
     * Cell list
     */
    private readonly cells: RowCell[] = [];

    /**
     * Cell selection levels
     */
    private selectionMap: number[] = [];

    /**
     * View was initialized
     */
    private _viewInitialized: boolean = false;

    /**
     * One of the cells was in edit mode before destroying the row
     */
    private _wasEditor: CellPosition = null;

    /**
     *  Row height in previous render
     */
    protected _height0: number = null;

    /**
     * Locale in previous render
     */
    private _locale0: string = '';

    // ÐÑÐ¸Ð¼ÐµÐ½ÐµÐ½Ð¸Ðµ ÑÑÐ¸Ð»ÐµÐ¹ Ð¿Ð¾Ð¼ÐµÑÐµÐ½Ð½ÑÑ Ð·Ð°Ð¿Ð¸ÑÐµÐ¹
    private _checkedAppearance = false;

    // ÐÐ¾Ð´Ð¿Ð¸ÑÐºÐ¸ Ð½Ð° Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð¸Ñ Ð² ÑÐµÐ´Ð°ÐºÑÐ¾ÑÐµ
    protected _subscribes: any[] = [];

    /**
     * ÐÑÐµÐ´ÑÐ´ÑÑÐµÐµ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ðµ Ð¾Ð±ÑÐµÐ³Ð¾ ÑÐµÐºÑÑÐ¾Ð²Ð¾Ð³Ð¾ ÑÐ¸Ð»ÑÑÑÐ°
     */
    private _filter0 = '';

    protected get st() {
      return this.state.settings;
    }

    protected get sta() {
      return this.state.settings.appearance;
    }

    protected get currentRowIndex() {
      return this.axI + this.layout.selection.displayedStartIndex;
    }

    protected get children() {
      return this.elementRef.nativeElement.children;
    }

    protected _editorRef: any = null;
    protected readonly _customCellRefs: any[] = [];
    protected readonly _skips: { element: HTMLElement, fromIndex: number, toIndex: number }[] = [];

    public firstCellRect(): any {
      for (let i = 0; i < this.children.length; i++) {
        const el = this.children[i];
        // Ð­ÑÐ¸ Ð²Ð¾Ð¾Ð±ÑÐµ Ð½Ðµ ÑÑÐ¸ÑÐ°ÐµÐ¼.
        if (el.classList.contains('true-cell-indent')) {
          continue;
        }
        return el.getBoundingClientRect();
      }
      return null;
    }

    private firstCellClientHeight(): number {
      for (let i = 0; i < this.children.length; i++) {
        const el = this.children[i];
        if (el.classList.contains('true-cell-indent')) {
          continue; // Ð­ÑÐ¸ Ð¼Ð¾Ð³ÑÑ Ð±ÑÑÑ Ð±ÐµÐ· Ð±Ð¾ÑÐ´ÐµÑÐ¾Ð², Ð° Ð¾ÑÑÐ°Ð»ÑÐ½ÑÐµ Ñ Ð±Ð¾ÑÐ´ÐµÑÐ°Ð¼Ð¸
        }
        return el.clientHeight;
      }
      return null;
    }

    /**
     * ÐÐ¾Ð»ÑÑÐµÐ½Ð¸Ðµ Ð¿Ð¾Ð»Ñ Ð¿Ð¾ ÐºÐ¾Ð¾ÑÐ´Ð¸Ð½Ð°ÑÐ°Ð¼
     * @param  x ÐÐ¾Ð¾ÑÐ´Ð¸Ð½Ð°ÑÐ° Ð¿Ð¾Ð»Ñ
     * @return   [description]
     */
    public cellByXY(x: number, y: number): string {
      let xx = this.cells[0].element.getBoundingClientRect().left;
      for (let i = 0; i < this.cells.length; i++) {
          const cell = this.cells[i];
          const col = this.state.columnByFieldName(cell.fieldName);
          const ww = !cell.skipped ? cell.element.offsetWidth : col.displayedWidth;
          if (x >= xx && x < xx + ww) {
            return cell.fieldName;
          }
          xx += ww;
      }
      return '';
    }

    /**
     * ÐÑÐ·ÑÐ²Ð°ÐµÑÑÑ Ð´Ð»Ñ Ð¿Ð¾Ð»ÑÑÐµÐ½Ð¸Ñ Ð¥-ÐºÐ¾Ð¾ÑÐ´Ð¸Ð½Ð°ÑÑ ÑÑÐµÐ¹ÐºÐ¸ Ñ ÑÐµÐ»ÑÑ Ð¿ÑÐ¾ÐºÑÑÑÐºÐ¸
     * ÐºÐ¾Ð¼Ð¿Ð¾Ð½ÐµÐ½ÑÐ° Ðº Ð·Ð°Ð´Ð°Ð½Ð½Ð¾Ð¹ ÑÑÐµÐ¹ÐºÐµ
     * @param  fieldName ÐÐ°Ð¸Ð¼ÐµÐ½Ð¾Ð²Ð°Ð½Ð¸Ðµ Ð¿Ð¾Ð»Ñ ÑÑÐµÐ¹ÐºÐ¸
     * @return           ÐÐ¾Ð¾ÑÐ´Ð¸Ð½Ð°ÑÑ Ð»ÐµÐ²Ð¾Ð³Ð¾ Ð¸ Ð¿ÑÐ°Ð²Ð¾Ð³Ð¾ ÐºÑÐ°Ñ
     */
    public cellHorizontalPos(fieldName: string): { l: number, r: number } {
      const x0 = this.cells[0].element.offsetLeft;
      let xx = x0;
      for (let i = 0; i < this.cells.length; i++)
      {
          const cell = this.cells[i];
          const col = this.state.columnByFieldName(cell.fieldName);
          // ÐÐ¾Ð»ÑÑÐ¸ÑÑ clientWidth
          const ww = !cell.skipped ? cell.element.offsetWidth : col.displayedWidth;
          if (cell.fieldName === fieldName) {
            return {
              // ÐÐ»Ñ Ð¿ÐµÑÐ²Ð¾Ð¹ ÐºÐ¾Ð»Ð¾Ð½ÐºÐ¸ ÑÐºÐ°Ð·ÑÐ°ÐµÐ¼ Ð½ÑÐ»ÐµÐ²Ð¾Ð¹ offset, ÑÑÐ¾Ð±Ñ Ð¿ÑÐ¸ Ð½Ð°Ð»Ð¸ÑÐ¸Ð¸ indents
              // Ð¿ÑÐ¾ÐºÑÑÑÐºÐ° Ð¾ÑÑÑÐµÑÑÐ²Ð»ÑÐ»Ð°ÑÑ Ðº ÑÐ°Ð¼Ð¾Ð¼Ñ Ð½Ð°ÑÐ°Ð»Ñ
              l: i === 0 ? 0 : xx,
              r: xx + ww
            };
          }
          xx += ww;
      }
      return null;
    }

    // ÐÑÐ¸ÑÑÐºÐ° Ð²ÑÐ´ÐµÐ»ÐµÐ½Ð¸Ñ
    public clearSelection() {
      this.cells.forEach((cell)=> {
        cell.setSelected(0);
        cell.setCellFocused(false);
      });
    }

    // ÐÐµÐ½ÐµÑÐ¸ÑÑÐµÐ¼ Ð¿Ð»Ð°Ð½ Ð²ÑÐ´ÐµÐ»ÐµÐ½Ð¸Ñ
    private getSelectionMap(sel: GridLayoutSelection, rowChecked: boolean = false): any[] {

      const sels: number[] = [];

      let sel0 = 0;

      for (let i = 0; i < this.cells.length; i++) {
        sels.push(sel0);
      }

      for (let j = 0; j < sel.ranges.length; j++) {
        const selection = sel.ranges[j];

        // ÐÐ¾ Ð²ÐµÑÑÐ¸ÐºÐ°Ð»Ð¸
        let minI = selection.rowIndex;
        let maxI = selection.rowIndex + selection.rangeY;

        // ÐÐµÐ½ÑÐµÐ¼ Ð¼ÐµÑÑÐ°Ð¼Ð¸ 'to' and 'from'
        if (minI > maxI) {
          const t = maxI;
          maxI = minI; minI = t;
        }

        if (this.currentRowIndex < minI || this.currentRowIndex > maxI) {
          continue; // Ð ÑÑÐ¾Ð¹ ÑÑÑÐ¾ÐºÐµ Ð½Ð¸ÑÐµÐ³Ð¾ Ð½Ðµ Ð²ÑÐ´ÐµÐ»ÐµÐ½Ð¾
        }

        // ÐÐ¾ Ð³Ð¾ÑÐ¸Ð·Ð¾Ð½ÑÐ°Ð»Ð¸
        const fromIndex = selection.columnIndex;
        const toIndex = fromIndex + selection.rangeX;
        const selectedColumnIndex = fromIndex;

        for (let i = 0; i < this.cells.length; i++) {
          if (i >= fromIndex && i <= toIndex || i <= fromIndex && i >= toIndex) {
            sels[i]++;
          }
        }
      }
      return sels;
    }

    protected setCellSelection(cell: RowCell, idx: number): number {
      const sel = this.layout.selection;
      const selected = this.selectionMap[idx];
      const cellFocused = this.currentRowIndex === sel.focusedRowIndex && idx === sel.focusedColumnIndex;
      cell.setSelected(selected);
      cell.setCellFocused(cellFocused);
      return selected;
    }

    // Ð£ÑÑÐ°Ð½Ð°Ð²Ð»Ð¸Ð²Ð°ÐµÐ¼ Ð²ÑÐ´ÐµÐ»ÐµÐ½Ð¸Ðµ
    public setSelection() {

      const sel = this.layout.selection;
      const rowChecked = this.state.check.isRowChecked(this.row);

      if (this.state.sta.checkedRowClass != '' && (this.state.sta.enableCheckedAppearance || this._checkedAppearance)) {
        if (rowChecked && this.state.sta.enableCheckedAppearance) {
          this.elementRef.nativeElement.classList.add(this.sta.checkedRowClass);
        } else {
          this.elementRef.nativeElement.classList.remove(this.sta.checkedRowClass);
        }
      }

      this._checkedAppearance = this.sta.enableCheckedAppearance;

      if (sel.ranges.length < 1 && !rowChecked) {
        this.clearSelection();
        return;
      }

      this.selectionMap = this.getSelectionMap(sel, rowChecked);
      this.cells.forEach((cell, i) => {
        this.setCellSelection(cell, i);
      });
    }

    // ÐÐ¾Ð»Ð½Ð°Ñ Ð¾ÑÐ¸ÑÑÐºÐ° ÑÑÑÐ¾ÐºÐ¸
    protected clear(saveH: boolean = false) {

      if (saveH) {
        // Ð¡Ð¾ÑÑÐ°Ð½ÑÐµÐ¼ Ð²ÑÑÐ¾ÑÑ ÑÑÑÐ¾ÐºÐ¸ Ð´Ð»Ñ ÑÐµÐ´Ð°ÐºÑÐ¾ÑÐ°
        this._height0 = this.firstCellClientHeight();
      } else {
        // Ð¡Ð±ÑÐ°ÑÑÐ²Ð°ÐµÐ¼ Ð²ÑÑÐ¾ÑÑ ÑÑÑÐ¾ÐºÐ¸, ÑÑÐ¾Ð±Ñ Ð½Ðµ Ð±ÑÐ»Ð¾ Ð½ÐµÐ´Ð¾ÑÑÐ¾Ð²ÐµÑÐ½Ð¾Ð¹ Ð¸Ð½ÑÐ¾ÑÐ¼Ð°ÑÐ¸Ð¸
        this._height0 = null;
      }

      this._subscribes.forEach(s => s.unsubscribe());
      this._subscribes = [];

      this.cells.splice(0, this.cells.length);

      let itemsToRemove = [];
      // Fastest way
      this.elementRef.nativeElement.innerHTML = '';

      if (this._customCellRefs.length > 0) {
        this._customCellRefs.forEach(r => r.destroy());
        this._customCellRefs.splice(0, this._customCellRefs.length);
      }

      this.clearEditor();
    }

    protected clearEditor() {
      this._height0 = this.firstCellClientHeight();

      if (this._editorRef && this._wasEditor) {
        const cp = this._wasEditor;
        this.cells.forEach(cell => {
          if (cell.fieldName === cp.fieldName && this.rowData === cp.row) {
            cell.element.style.height = 'unset';
            this._renderer.removeClass(cell.element, 'true-cell-input');
            let itemsToRemove = [];
            let i = 0;
            while (i < cell.element.children.length) {
              itemsToRemove.push(cell.element.children[i]);
              i++;
            }
            itemsToRemove.forEach(item => this._renderer.removeChild(cell.element, item));

            const col = this.state.columnByFieldName(cp.fieldName);
            const rowData = this.rowData;
            const v = this.rowData[col.fieldName];
            const v_displayed = this.getDisplayedValue(col, rowData, v);
            this.fillCell(cell, col, rowData, false, v, v_displayed);
            cell.value = v;
          }
          /*
              this._editorRef.destroy();
              this._editorRef = null;
          */

          this._wasEditor = null;
        });
      }
    }

    protected renderStub(rowData: any, cell: RowCell, col: Column) {
      if (col.isBoolean) {
        return;
      }
      const divEl = this._renderer.createElement('span');
      const w = 20 + Math.random() * 40;
      divEl.style.width = w + '%';
      this._renderer.appendChild(cell.element, divEl);
      this._renderer.addClass(divEl, 'true-stub');
    }

    // Ð§ÐµÐºÐ±Ð¾ÐºÑ ÑÐ¾Ð·Ð´Ð°ÐµÑÑÑ Ð¿ÑÑÐ¼Ð¾ Ð² td. ÐÐ»Ñ ÑÐ¾Ð³Ð¾, ÑÑÐ¾Ð±Ñ ÐµÐ³Ð¾ ÑÐ¸ÑÐ¸Ð½Ð° Ð½Ðµ Ð·Ð°Ð²Ð¸ÑÐµÐ»Ð°
    // Ð¾Ñ Ð½Ð°Ð»Ð¸ÑÐ¸Ñ Ð±Ð¾ÑÐ´ÐµÑÐ¾Ð² Ð¸ ÑÐ¸ÑÐ¸Ð½Ñ ÑÑÐµÐ¹ÐºÐ¸, Ð±ÑÐ»Ð¾ Ð±Ñ Ð½ÐµÐ¿Ð»Ð¾ÑÐ¾ ÑÐ¾Ð·Ð´Ð°ÑÑ
    // Ð¿ÑÐ¾Ð¼ÐµÐ¶ÑÑÐ¾ÑÐ½ÑÐ¹ div Ñ display: flex. ÐÐ¾ Ð²ÑÐ·Ð¾Ð² ÐµÑÐµ Ð¾Ð´Ð½Ð¾Ð³Ð¾ createElement
    // ÑÐ¸Ð»ÑÐ½Ð¾ Ð·Ð°Ð¼ÐµÐ´Ð»ÑÐµÑ Ð¿ÑÐ¾ÑÐµÑÑ ÑÐ¾Ð·Ð´Ð°Ð½Ð¸Ñ ÑÑÑÐ¾ÐºÐ¸.
    protected renderBoolean(rowData: any, cell: RowCell, col: Column, v: any) {
      const divEl = this._renderer.createElement('div');
      if (this.st.canEditColumnCell(col)) {
        this._renderer.addClass(divEl, this.sta.checkboxClass);
        this._renderer.listen(divEl, 'mousedown', (e: any) => e.stopPropagation());
        this._renderer.listen(divEl, 'dblclick', (e: any) => e.stopPropagation());
        this._renderer.listen(divEl, 'click', (e: any) => {
          this.toggleCheckbox.emit({row: rowData, fieldName: col.fieldName});
          e.stopPropagation();
        });
        this._renderer.appendChild(cell.element, divEl);
      } else {
        const classes = this.sta.booleanClass.trim().split(' ');
        classes.forEach(s => this._renderer.addClass(divEl, s));
        this._renderer.appendChild(cell.element, divEl);
      }
      cell.cbElement = divEl;
      cell.setChecked(v);
    }

    protected renderCheckbox(cell: RowCell, col: Column, v: any) {
      const divEl = this._renderer.createElement('div');

      this._renderer.addClass(divEl, this.sta.checkboxClass);
      if (!this.st.checkByCellClick) {
        this._renderer.listen(divEl, 'mousedown', (e: any) => e.stopPropagation());
        this._renderer.listen(divEl, 'dblclick', (e: any) => e.stopPropagation());
        this._renderer.listen(divEl, 'click', (e: any) => {
          this.toggleCheckbox.emit({ row: this.row, fieldName: col.fieldName });
          e.stopPropagation();
        });
      }
      cell.cbElement = divEl;
      this._renderer.appendChild(cell.element, divEl);
      cell.setChecked(v);
    }

    protected getEditorComponentType(col: Column): any {
      let editorType: any = EditorTextComponent;

      if (col.type === ColumnType.DATETIME) {
        editorType = EditorDateComponent;
      }

      if (col.type === ColumnType.NUMBER) {
        editorType = EditorNumberComponent;
      }

      if (col.type === ColumnType.UNSAFE_HTML) {
        editorType = EditorSelectTrueComponent;
      }

      if (col.editorComponentType !== null) {
        editorType = col.editorComponentType;
      }

      return editorType;
    }

    protected getDh(): number {
      // ÐÐ´Ð¸Ð½ Ð¿Ð¸ÐºÑÐµÐ» Ð´Ð¾Ð±Ð°Ð²Ð¸Ð¼, ÐµÑÐ»Ð¸ Ð²Ð¸Ð´Ð½Ñ Ð³Ð¾ÑÐ¸Ð·Ð¾Ð½ÑÐ°Ð»ÑÐ½ÑÐµ Ð»Ð¸Ð½Ð¸Ð¸
      let dh = 0;
      if (this.axI !== 0 && !this.state.IE && this.sta.horizontalLines) {
        dh = 1;
      }
      return dh;
    }

    protected setEditorHeight(cell : RowCell) {
      // Ð¢ÐµÑÑ, ÐºÐ¾Ð³Ð´Ð° Ð²ÑÑ ÑÐµÐ´Ð°ÐºÑÐ¾ÑÑ
      if (this._height0 === null && this.state.ui.editorHeight !== null) {
        // ÐÑÐ»Ð¸ Ð² ÑÐ¾ÑÑÐ¾ÑÐ½Ð¸Ð¸ ÑÐ¾ÑÑÐ°Ð½ÐµÐ½Ð° Ð²ÑÑÐ¾ÑÐ°, ÑÐ¾ Ð¸ÑÐ¿Ð¾Ð»ÑÐ·ÑÐµÐ¼ ÐµÑ
        this._height0 = this.state.ui.editorHeight;
      } else {
        // ÐÐ½Ð°ÑÐµ Ð½Ð°Ð¾Ð±Ð¾ÑÐ¾Ñ - ÑÐ¾ÑÑÐ°Ð½ÑÐµÐ¼ Ð²ÑÑÐ¾ÑÑ
        this.state.ui.editorHeight = this._height0;
      }
      if (this._height0 >= this.state.settings.rowHeight) {
        cell.element.style.height = (this._height0 - 1) + 'px';
      }
    }

    protected renderEditor(rowData: any, cell: RowCell, col: Column, v: any) {
      this._renderer.addClass(cell.element, 'true-cell-input');

      const editorType = this.getEditorComponentType(col);
      const componentFactory = this._cfResolver.resolveComponentFactory(editorType);

      this._editorRef = componentFactory.create(this._injector, [], cell.element);

      this._appRef.attachView(this._editorRef.hostView);

      // Initialization
      this._editorRef.instance.state = this.state;
      this._editorRef.instance.column = col;
      this._editorRef.instance.row = rowData;

      this.setEditorHeight(cell);

      const dh = this.getDh();

      // ÐÐ·Ð¼ÐµÐ½ÐµÐ½Ð¸Ðµ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ñ. Ð¡Ð¾ÑÑÐ°Ð½ÑÐµÐ¼ Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð½Ð¾Ðµ.
      const s_change = this._editorRef.instance.change.subscribe((v: any) => {
        this.state.ui.editorValue = v;
      });

      // ÐÐ¾Ð´ÑÐ²ÐµÑÐ¶Ð´ÐµÐ½Ð¸Ðµ ÑÐµÐ´Ð°ÐºÑÐ¸ÑÐ¾Ð²Ð°Ð½Ð¸Ñ. ÐÑÐ¿ÑÐ°Ð²Ð»ÑÐµÐ¼ Ð² Ð´Ð°Ð½Ð½ÑÐµ
      const s_commit = this._editorRef.instance.commit.subscribe((e: any) => {
        this.state.ui.commitEditor(this.row, col.fieldName, e);
      });

      // ÐÑÐ¼ÐµÐ½Ð° ÑÐµÐ´Ð°ÐºÑÐ¸ÑÐ¾Ð²Ð°Ð½Ð¸Ñ
      const s_cancel = this._editorRef.instance.cancel.subscribe(() => {
        let cp: CellPosition = this._wasEditor.clone();
        this.state.ui.stopEditing(cp, true, true);
      });

      this._editorRef.instance.init(this.state.ui.editorValue,
                                    this.state.ui.editorValueChanged,
                                    this._height0 + dh,
                                    this.state.IE,
                                    this.state.ui.editorWasShown);

      this.state.ui.editorWasShown = true;
      // Persisting last editor state
      if (this.state.ui.editor) {
        this._wasEditor = this.state.ui.editor.clone();
      }

      //
      this._subscribes.push(s_change);
      this._subscribes.push(s_commit);
      this._subscribes.push(s_cancel);
    }

    // ÐÐ¾ÐºÐ° ÑÐ°Ðº
    private renderCustomCell(rowData: any, cell: RowCell, col: Column, v: any, componentType: any = CellHtmlComponent) {
      this._renderer.addClass(cell.element, 'true-cell-custom');

      const componentFactory = this._cfResolver.resolveComponentFactory(componentType);
      const cr: any = componentFactory.create(this._injector, [], cell.element);

      // Initialization
      cr.instance.state = this.state;
      cr.instance.column = col;
      cr.instance.row = rowData;

      this._appRef.attachView(cr.hostView);

      cr.instance.init(this.getDisplayedValue(col, rowData, v));

      // ÐÐ¾Ð´ÑÐ²ÐµÑÐ¶Ð´ÐµÐ½Ð¸Ðµ ÑÐµÐ´Ð°ÐºÑÐ¸ÑÐ¾Ð²Ð°Ð½Ð¸Ñ. ÐÑÐ¿ÑÐ°Ð²Ð»ÑÐµÐ¼ Ð² Ð´Ð°Ð½Ð½ÑÐµ
      const s = cr.instance.event.subscribe((e: any)=> {
        // Ð²ÑÐ·ÑÐ²Ð°ÐµÐ¼ Ð² ÑÑÑÐ¹ÑÐµ
        this.state.emitCustomCellEvent(e);
      });

      this._customCellRefs.push(cr);
      this._subscribes.push(s);
    }

    protected getDisplayedValue(col: Column, rowData: any, v: any) {
       return this.state.dataSource.displayedValue(col, v, rowData);
    }

    protected fillCell(cell: RowCell, col: Column, rowData: any, isFirst: boolean, v: any, v_displayed: any): RowCell {

      const classes = this.state.st.cellClass(col).trim().split(' ');
      classes.forEach(s => {
        if (s !== '') {
          this._renderer.addClass(cell.element, s);
        }
      });

      if (col.classField !== '' && rowData[col.classField]) {
        this._renderer.addClass(cell.element, rowData[col.classField]);
      }

      cell.rendered = true;
      cell.fieldName = col.fieldName;

      if (rowData.__ax === 'empty') {
        this.renderStub(rowData, cell, col);
        return cell;
      }

      const tdEl = cell.element;

      if (col.isCheckbox) {
        // ÐÐ¾Ð¼ÐµÑÐ°ÑÑÑÑ Ð½Ðµ ÑÐ¾Ð»ÑÐºÐ¾ Ð´Ð°Ð½Ð½ÑÐµ, Ð½Ð¾ Ð¸ Ð·Ð°Ð³Ð¾Ð»Ð¾Ð²ÐºÐ¸ Ð³ÑÑÐ¿Ð¿. ÐÐ¾ÑÑÐ¾Ð¼Ñ
        // Ð°Ð¿Ð´ÐµÐ¹ÑÐ¸ÑÑÑ ÑÑÑÐ¾ÐºÐ° Ð¸Ð· ÑÐµÐ·ÑÐ»ÑÑÐ¸ÑÑÑÑÐµÐ³Ð¾ Ð½Ð°Ð±Ð¾ÑÐ° Ð´Ð°Ð½Ð½ÑÑ
        this.renderCheckbox(cell, col, v);
        return cell;
      }

      if (col.isBoolean) {
        // ÐÐ¾Ð¶ÐµÑ Ð±ÑÑÑ Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð¾ ÑÐ¾Ð»ÑÐºÐ¾ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ðµ Ð¸ÑÑÐ¾Ð´Ð½ÑÑ Ð´Ð°Ð½Ð½ÑÑ, Ð½Ð¾ Ð½Ðµ
        // Ð·Ð°Ð³Ð¾Ð»Ð¾Ð²ÐºÐ¾Ð² Ð³ÑÑÐ¿Ð¿. ÐÐ¾ÑÑÐ¾Ð¼Ñ Ð¾ÑÐ¿ÑÐ°Ð²Ð»ÑÐµÑÑÑ Ð¸ÑÑÐ¾Ð´Ð½Ð°Ñ ÑÑÑÐ¾ÐºÐ°
        this.renderBoolean(rowData, cell, col, v);
        return cell;
      }

      if (col.type === ColumnType.REFERENCE) {
        this.renderCustomCell(rowData, cell, col, v, CellRefComponent);
        return cell;
      }

      if (this.state.ui.editor !== null &&
          this.state.ui.editor.fieldName === col.fieldName &&
          this.state.ui.editor.row === rowData) {
        // ÐÐ°Ð³Ð¾Ð»Ð¾Ð²Ð¾Ðº Ð³ÑÑÐ¿Ð¿Ñ Ð½Ðµ Ð¼Ð¾Ð¶ÐµÑ Ð±ÑÑÑ Ð¾ÑÑÐµÐ´Ð°ÐºÑÐ¸ÑÐ¾Ð²Ð°Ð½, Ð¾ÑÐ¿ÑÐ°Ð²Ð»ÑÑÑÑÑ Ð¸ÑÑÐ¾Ð´Ð½ÑÐµ
        // Ð´Ð°Ð½Ð½ÑÐµ
        this.renderEditor(rowData, cell, col, v);
        return cell;
      }

      if (col.cellComponentType !== null) {
        // Ð§ÑÐ¾Ð±Ñ ÐºÐ°ÑÑÐ¾Ð¼Ð½Ð°Ñ ÑÑÐµÐ¹ÐºÐ° Ð½Ðµ Ð¿ÑÑÐ°Ð»Ð°ÑÑ, Ð¾ÑÐ¿ÑÐ°Ð²Ð»ÑÑÑÑÑ Ð¸ÑÑÐ¾Ð´Ð½ÑÐµ Ð´Ð°Ð½Ð½ÑÐµ.
        // ÐÑÐ»Ð¸ ÑÑÐµÐ¹ÐºÐ° Ð±ÑÐ´ÐµÑ ÑÐ¾Ð·Ð´Ð°Ð½Ð° Ð² Ð·Ð°Ð³Ð¾Ð»Ð¾Ð²ÐºÐµ Ð³ÑÑÐ¿Ð¿Ñ - Ð¼Ñ ÑÑÐ¾ Ð½Ðµ Ð¿ÑÐµÐ´ÑÑÐ¼Ð¾ÑÑÐµÐ»Ð¸.
        this.renderCustomCell(rowData, cell, col, v, col.cellComponentType);
        return cell;
      }

      if (col.type === ColumnType.HTML) {
        // Ð§ÑÐ¾Ð±Ñ ÐºÐ°ÑÑÐ¾Ð¼Ð½Ð°Ñ ÑÑÐµÐ¹ÐºÐ° Ð½Ðµ Ð¿ÑÑÐ°Ð»Ð°ÑÑ, Ð¾ÑÐ¿ÑÐ°Ð²Ð»ÑÑÑÑÑ Ð¸ÑÑÐ¾Ð´Ð½ÑÐµ Ð´Ð°Ð½Ð½ÑÐµ.
        // ÐÑÐ»Ð¸ ÑÑÐµÐ¹ÐºÐ° Ð±ÑÐ´ÐµÑ ÑÐ¾Ð·Ð´Ð°Ð½Ð° Ð² Ð·Ð°Ð³Ð¾Ð»Ð¾Ð²ÐºÐµ Ð³ÑÑÐ¿Ð¿Ñ - Ð¼Ñ ÑÑÐ¾ Ð½Ðµ Ð¿ÑÐµÐ´ÑÑÐ¼Ð¾ÑÑÐµÐ»Ð¸.
        this.renderCustomCell(rowData, cell, col, v);
        return cell;
      }

      let html: boolean = col.type === ColumnType.UNSAFE_HTML;
      if (this.state.dataSource.searchString !== '' && this.state.st.searchHighlight) {
        html = true;
      }

      if (html) {
        const s = this.state.dataSource.searchString;
        tdEl.innerHTML = CellHighlighter.highlight(s, col, v, v_displayed);
      } else {
        tdEl.innerText = v_displayed;
      }

      return cell;
    }

    private needRender(col: Column, xPos: number, isFirst: boolean) {
      let render = true;
      // ÐÐµ Ð²ÑÐµ ÑÑÐµÐ¹ÐºÐ¸ ÑÐµÐ½Ð´ÐµÑÑÑÑÑ ÑÐ¾Ð»ÑÐºÐ¾ Ð¿ÑÐ¸ Ð½Ð°ÑÑÑÐ¾Ð¹ÐºÐµ RenderMode.VISIBLE
      if (this.st.renderMode !== RenderMode.VISIBLE) {
        return render;
      }

      if (this.viewPortLeft >= 0 && !isFirst) {
        // Ð£ÑÐ¸ÑÑÐ²Ð°ÐµÐ¼ Ð²ÑÑÐ¿Ð¾ÑÑ
        if (xPos > (this.viewPortLeft + this.viewPortWidth)) {
          // ÐÑ Ð½Ð°ÑÐ¾Ð´Ð¸Ð¼ÑÑ Ð¿ÑÐ°Ð²ÐµÐµ
          render = false;
        }
        if ((xPos + col.headerWidth) < this.viewPortLeft) {
          // ÐÑ Ð½Ð°ÑÐ¾Ð´Ð¸Ð¼ÑÑ Ð»ÐµÐ²ÐµÐµ
          render = false;
        }
      }
      return render;
    }

    protected createTd(span: number): HTMLElement {
      const tdEl = this._renderer.createElement('td');

      if (span > 1) {
        this._renderer.setAttribute(tdEl, 'colspan', span + '');
      }

      return tdEl;
    }

    /**
     * Ð¡Ð¾Ð·Ð´Ð°Ð½Ð¸Ðµ ÑÑÐµÐ¹ÐºÐ¸
     * @param  col   ÐÐ¾Ð»Ð¾Ð½ÐºÐ°
     * @param  span  ÐÐ¾Ð»Ð¸ÑÐµÑÑÐ²Ð¾ Ð¾Ð±ÑÐµÐ´Ð¸Ð½ÐµÐ½Ð½ÑÑ ÑÑÐµÐµÐº
     * @return       ÐÐ±ÑÐµÐºÑ RowCell
     */
    protected createCell(col: Column, span: number = 1, isFirst: boolean = false, xPos: number = -1, render: boolean = true): RowCell {

      const rowData = this.rowData;
      const v = rowData !== null ? rowData[col.fieldName] : null;
      const cell = new RowCell(col.fieldName, v, null);

      cell.element = this.createTd(span);

      if (render) {
        const v_displayed = this.getDisplayedValue(col, rowData, v);
        this.fillCell(cell, col, rowData, isFirst, v, v_displayed);
      }

      return cell;
    }

    // ÐÐ»Ñ Ð´ÐµÑÐµÐ²Ð° - Ð¿ÑÐ¾Ð²ÐµÑÐºÐ° Ð½ÐµÐ¾Ð±ÑÐ¾Ð´Ð¸Ð¼Ð¾ÑÑÐ¸ Ð¾Ð±ÑÐµÐ´Ð¸Ð½ÐµÐ½Ð¸Ñ ÑÑÐµÐµÐº
    protected needApplySpan(): number {
      return 1;
    }

    private createSkipCell(skipCount: number): HTMLElement {
      const tdEl = this._renderer.createElement('td');
      this._renderer.setAttribute(tdEl, 'colspan', skipCount + '');
      return tdEl;
    }

    protected renderRowEditor() {
      for (let i = 0; i < this.layout.columns.length; i++) {
        const col: Column = this.layout.columns[i];

        if (col.isBoolean) {
          continue;
        }

        const cell = this.cells[i];

        if (this.state.ui.editor !== null &&
            this.state.ui.editor.fieldName === col.fieldName &&
            this.state.ui.editor.row === this.rowData) {
          // ÐÐ°Ð³Ð¾Ð»Ð¾Ð²Ð¾Ðº Ð³ÑÑÐ¿Ð¿Ñ Ð½Ðµ Ð¼Ð¾Ð¶ÐµÑ Ð±ÑÑÑ Ð¾ÑÑÐµÐ´Ð°ÐºÑÐ¸ÑÐ¾Ð²Ð°Ð½, Ð¾ÑÐ¿ÑÐ°Ð²Ð»ÑÑÑÑÑ Ð¸ÑÑÐ¾Ð´Ð½ÑÐµ
          // Ð´Ð°Ð½Ð½ÑÐµ
          const v = this.rowData[col.fieldName];
          this.renderEditor(this.rowData, cell, col, v);
          return cell;
        }
      }
    }

    // ÐÐ°Ð¿Ð¾Ð»Ð½ÐµÐ½Ð¸Ðµ ÑÑÑÐ¾ÐºÐ¸ (ÑÐ¾Ð·Ð´Ð°Ð½Ð¸Ðµ ÑÐ°Ð·Ð¼ÐµÑÐºÐ¸ Ð¸ Ð²ÑÑÐ°Ð²ÐºÐ° Ð´Ð°Ð½Ð½ÑÑ)
    protected renderRow() {
      this._locale0 = this.locale;
      this._skips.splice(0, this._skips.length);
      this._wasEditor = null;

      if (!this.empty) {
        this._filter0 = this.state.dataSource.searchString;
      }

      // Ð£Ð´Ð°Ð»ÐµÐ½Ð¸Ðµ ÑÑÐ°ÑÑÑ ÑÑÐµÐµÐº
      this.cells.splice(0, this.cells.length);
      let firstCell = true;

      if (this.empty) {
        const tdEl = this._renderer.createElement('td');
        this._renderer.setAttribute(tdEl, 'colspan', this.layout.columns.length + '');
        this._renderer.appendChild(this.elementRef.nativeElement, tdEl);
        return;
      }
      // const dt = Date.now();
      let xPos = 0;
      let skipCount = 0;
      let skipStart = -1;
      let skipEnd = -1;

      for (let i = 0; i < this.layout.columns.length; i++) {
        const col: Column = this.layout.columns[i];
        // ÐÐ»Ñ Ð´ÐµÑÐµÐ²Ð°.. ÐÐ°Ð¼ Ð½ÑÐ¶ÐµÐ½ ÑÑÐ¾Ð²ÐµÐ½Ñ.. Ð ÑÐ°ÑÑÑÐ³Ð¸Ð²Ð°Ð½Ð¸Ðµ Ð¿ÐµÑÐ²Ð¾Ð¹ Ð½Ðµ ÑÐµÐºÐ±Ð¾ÐºÑÐ¾Ð²Ð¾Ð¹ ÐºÐ¾Ð»Ð¾Ð½ÐºÐ¸
        // ÐÐ¾ ÑÐ¾Ð»ÑÐºÐ¾ ÐµÑÐ»Ð¸ Ð¼Ñ Ð½Ð°ÑÐ¾Ð´Ð¸Ð¼ÑÑ Ð² ÑÐ°Ð¼Ð¾Ð¹ Ð»ÐµÐ²Ð¾Ð¹ ÑÐ°ÑÑÐ¸
        let span = 1;
        const f = firstCell;
        if (firstCell && !col.isCheckbox) {
          span = this.needApplySpan();
          firstCell = false;
        }
        // Done
        const needRender = this.needRender(col, xPos, f);
        const cell = this.createCell(col, span, f, xPos, needRender);
        this.cells.push(cell);

        if (needRender) {
          if (skipCount > 0) {
            // Was skips
            const skipTd = this.createSkipCell(skipCount);
            this._renderer.appendChild(this.elementRef.nativeElement, skipTd);
            skipCount = 0;
            this._skips.push({ element: skipTd, fromIndex: skipStart, toIndex: skipEnd });
          }
          // Very resource intensive operation
          this._renderer.appendChild(this.elementRef.nativeElement, cell.element);
        } else {
          if (skipCount === 0) {
            skipStart = i;
          }
          skipEnd = i;
          cell.skipped = true;
          skipCount++;
        }
        xPos += col.headerWidth;
      }

      if (skipCount > 0) {
        const skipTd = this.createSkipCell(skipCount);
        this._renderer.appendChild(this.elementRef.nativeElement, skipTd);
        this._skips.push({ element: skipTd, fromIndex: skipStart, toIndex: skipEnd });
      }

      this.setSelection();
      this.setDisabledFields();

      this._left_rendered = this.viewPortLeft;
      this._right_rendered = this.viewPortLeft + this.viewPortWidth;
    }

    private nextEl(el: HTMLElement): HTMLElement {
      let found = false;
      for (let i = 0; i < this.children.length; i++) {
        if (this.children[i] === el) {
          found = true;
          continue;
        }
        if (found) {
          return this.children[i];
        }
      }
      return null;
    }

    protected unskip(cell: RowCell, i: number) {

      for(let j = 0; j < this._skips.length; j++) {

        const skip = this._skips[j];

        if (i >= skip.fromIndex && i <= skip.toIndex) {
          let insertBeforeEl: HTMLElement = null;
          if (i === skip.fromIndex) {
            // ÐÐ¾Ð±Ð°Ð²Ð»ÑÐµÐ¼ ÑÐ»ÐµÐ²Ð°
            skip.fromIndex++;
            this._renderer.insertBefore(this.elementRef.nativeElement, cell.element, skip.element);
          } else {
            if (i === skip.toIndex) {
              // ÐÐ¾Ð±Ð°Ð²Ð»ÑÐµÐ¼ ÑÐ¿ÑÐ°Ð²Ð°
              skip.toIndex--;
              const nextEl = this.nextEl(skip.element)
              if (nextEl !== null) {
                this._renderer.insertBefore(this.elementRef.nativeElement, cell.element, nextEl);
              } else {
                this._renderer.appendChild(this.elementRef.nativeElement, cell.element);
              }
            } else {
              // ÐÐ¾Ð±Ð°Ð²Ð»ÑÐµÐ¼ Ð¿Ð¾ÑÐµÑÐµÐ´Ð¸Ð½Ðµ
              // Ð¡Ð¾Ð·Ð´Ð°ÐµÐ¼ Ð½Ð¾Ð²ÑÐ¹ ÑÐºÐ¸Ð¿...
              let newEl = this.createSkipCell(i - skip.fromIndex);
              this._renderer.insertBefore(this.elementRef.nativeElement, cell.element, skip.element);
              this._renderer.insertBefore(this.elementRef.nativeElement, newEl, cell.element);
              this._skips.push({ element: newEl, fromIndex: skip.fromIndex, toIndex: i-1 });
              skip.fromIndex = i + 1;
            }
          }

          if (skip.fromIndex > skip.toIndex) {
            // Ð£Ð´Ð°Ð»ÑÐµÐ¼ ÑÑÐ¾Ñ Ð¿ÑÐ¾Ð¿ÑÑÐº
            this._skips.splice(j, 1);
            this._renderer.removeChild(this.elementRef.nativeElement, skip.element);
          } else {
            // ÐÐµÐ½ÑÐµÐ¼ ÐµÐ³Ð¾ ÑÐ¿Ð°Ð½
            this._renderer.setAttribute(skip.element, 'colspan', (skip.toIndex - skip.fromIndex + 1) + '');
          }
          cell.skipped = false;
          break;
        }
      }
    }

    protected renderByViewPort() {

      const rowData = this.rowData;
      const right = this.viewPortLeft + this.viewPortWidth;
      const left = this.viewPortLeft;

      let xPos = 0;
      // ÐÐµÑÐ²Ð°Ñ ÑÑÐµÐ¹ÐºÐ° Ð²ÑÐµÐ³Ð´Ð° Ð¾ÑÑÐµÐ½Ð´ÐµÑÐµÐ½Ð°
      for (let i = 0; i < this.layout.columns.length; i++) {

        // ÐÐ¾Ð´Ð³Ð¾ÑÐ°Ð²Ð»Ð¸Ð²Ð°ÐµÐ¼ Ð´Ð°Ð½Ð½ÑÐµ
        const col: Column = this.layout.columns[i];
        const cell = this.cells[i];

        if (!cell) {
          continue;
        }

        // Ð ÐµÑÐ°ÐµÐ¼, ÑÐµÐ½Ð´ÐµÑÐ¸ÑÑ Ð¸Ð»Ð¸ Ð½ÐµÑ
        let needRender = this.needRender(col, xPos, false);

        // ÐÑÐ¶Ð½Ð¾ Ð¿Ð¾ÑÐ»ÐµÐ´Ð¾Ð²Ð°ÑÐµÐ»ÑÐ½Ð¾ Ð´Ð¾Ð±Ð°Ð²Ð»ÑÑÑ...
        if (needRender && i > 0) {
          if (!cell.rendered) {
            const v = cell.value;
            const v_displayed = this.getDisplayedValue(col, rowData, v);
            this.fillCell(cell, col, rowData, i === 0, v, v_displayed);
          }

          if (cell.skipped) {
            this.unskip(cell, i);
          }
        }
        xPos += col.headerWidth;
      }

      if (this._right_rendered < (this.viewPortLeft + this.viewPortWidth)) {
        this._right_rendered = this.viewPortLeft + this.viewPortWidth;
      }

      if (this._left_rendered > this.viewPortLeft) {
        this._left_rendered = this.viewPortLeft;
      }
    }

    // ÐÑÐ¾Ð²ÐµÑÑÐµÐ¼, Ð½ÑÐ¶Ð½Ð¾ Ð»Ð¸ Ð¿ÐµÑÐµÑÐ¾Ð·Ð´Ð°ÑÑ Ð¿Ð¾ Ð¿ÑÐ¸ÑÐ¸Ð½Ðµ Ð²ÐºÐ»ÑÑÐµÐ½Ð¸Ñ Ð¸Ð»Ð¸ Ð²ÑÐºÐ»ÑÑÐµÐ½Ð¸Ñ ÑÐµÐ´Ð°ÐºÑÐ¾ÑÐ°
    private checkEditor(): boolean {

      if (this._wasEditor === null && this.state.ui.editor === null) {
        // ÐÑÑ Ð¾Ðº.
        // Ð ÐµÐ´Ð°ÐºÑÐ¾ÑÐ° Ð¸ Ð½Ðµ Ð±ÑÐ»Ð¾ Ð¸ Ð½Ðµ Ð½Ð°Ð¼ÐµÑÐ°ÐµÑÑÑ.
        return true;
      }

      if (this._wasEditor === null && this.state.ui.editor !== null) {
        // ÐÐµ Ð±ÑÐ»Ð¾, Ð½Ð¾ Ð¿Ð¾ÑÐ²Ð¸Ð»ÑÑ
        if (this.state.ui.editor.row !== this.rowData) {
          // ÐÐ¾ ÑÑÑÐ¾ÐºÐ° Ð½Ðµ Ð½Ð°ÑÐ°
          return true;
        }
      }

      if (this._wasEditor !== null && this.state.ui.editor !== null) {
        // ÐÑÐ» Ð¸ Ð½Ðµ ÑÑÐ°Ð»Ð¾
        if (this._wasEditor.row === this.state.ui.editor.row) {
          // Ð ÑÑÐ¾Ð¹ ÑÑÑÐ¾ÐºÐµ
          if (this._wasEditor.fieldName === this.state.ui.editor.fieldName) {
            return true; // ÐÐµ Ð¸Ð·Ð¼ÐµÐ½Ð¸Ð»Ð¾ÑÑ, Ð¾ÑÑÐ°Ð²Ð»ÑÐµÐ¼
          }
        }
      }
      return false;
    }

    /**
     * We check whether it is necessary to recreate the string when changing the
     * column. If there were simple changes (two columns swapped or one of them
     * just disappeared), we try to apply these changes without a full rerendering.
     * @return True if we don't need a rerender.
     */
    protected checkColumns(): boolean {

      if (!this.layout) {
        return false;
      }

      if (this.cells.length === 0 && !this.layout.columns) {
        return true;
      }

      // The current number of cells is more than 1 higher than the number
      // of columns.
      if (this.cells.length > (this.layout.columns.length + 1)) {
        return false;
      }

      if (this.cells.length < (this.layout.columns.length - 1)) {
        return false;
      }

      let i = 0;
      let changed = false;

      while (i < this.cells.length) {

        if (!this.layout || !this.layout.columns[i]) {
          return false;
        }

        if (this.state.isTree && this.layout.columns[i].fieldName !== this.cells[i].fieldName) {
          // For a tree, it wouldn't help if we just moved the columns around.
          return false;
        }

        if (this.layout.columns[i].fieldName !== this.cells[i].fieldName) {
          // Let's consider three scenarios so that we don't recreate the entire line...
          if (i < this.cells.length - 1 &&
              this.layout.columns[i].fieldName === this.cells[i + 1].fieldName) {
            // 1. The column is missing?
            // Removing the cell.
            const ww = this.cells[i].element.offsetWidth;
            this._renderer.removeChild(this.elementRef.nativeElement, this.cells[i].element);
            this.cells.splice(i, 1);
            this._skips.forEach(s => {
              if (s.toIndex > i) {
                s.toIndex--;
              }
              if (s.fromIndex > i) {
                s.fromIndex--;
              }
            });
            continue;
          } else {
            if (i < this.cells.length - 2 &&
                this.layout.columns[i].fieldName === this.cells[i + 2].fieldName) {
              // 1.1. Two columns are missing?
              // Remove one cell. The second one will be removed by the next iteration.
              this._renderer.removeChild(this.elementRef.nativeElement, this.cells[i].element);
              this.cells.splice(i, 1);
              this._skips.forEach(s => {
                if (s.toIndex > i) {
                  s.toIndex--;
                }
                if (s.fromIndex > i) {
                  s.fromIndex--;
                }
              });
              continue;
            } else {
              if (i < this.layout.columns.length - 1 &&
                  this.cells[i].fieldName === this.layout.columns[i + 1].fieldName) {
                // 2. New column?
                if (this.cells[i].skipped) {
                  return false;
                }
                this.viewPortLeft = -1;
                const cell = this.createCell(this.layout.columns[i]);
                cell.setDisabled(this.state.dragDrop.disabledFields.indexOf(cell.fieldName) >= 0);
                this._renderer.insertBefore(this.elementRef.nativeElement, cell.element, this.cells[i].element);
                this.cells.splice(i, 0, cell);
                this._skips.forEach(s => {
                  if (s.toIndex > i) {
                    s.toIndex++;
                  }
                  if (s.fromIndex > i) {
                    s.fromIndex++;
                  }
                });
                // Don't forget to reset selection
                changed = true;
                i++;
              } else {
                // Didn't help, we need to rerender!
                return false;
              }
            }
          }
        }
        i++;
      }

      // Render what fit on the screen after changing the columns
      this.renderByViewPort();
      if (changed) {
        this.setSelection();
      }

      return true;
    }

    protected checkValues(): boolean {

      if (!this.layout) {
        return false;
      }

      if (this.cells.length === 0 && !this.layout.columns) {
        return true;
      }

      // ÐÑÑÐ³Ð¾Ðµ ÐºÐ¾Ð»Ð¸ÑÐµÑÑÐ²Ð¾ ÑÑÐµÐµÐº
      if (this.cells.length !== this.layout.columns.length) {
        return false;
      }

      // ÐÑÐ¶Ð½Ð¾ Ð¾Ð±Ð½Ð¾Ð²Ð¸ÑÑ Ð¿Ð¾Ð´ÑÐ²ÐµÑÐºÑ
      if (this._filter0 !== this.state.dataSource.searchString) {
        return false;
      }

      let i = 0;
      while (i < this.cells.length) {
        const cell = this.cells[i];
        const rowData = this.rowData;
        const v = rowData !== null ? rowData[cell.fieldName] : null;
        if (v !== cell.value) {

          if (cell.cbElement) {
            cell.setChecked(this.row[cell.fieldName]);
          } else {
            // ÐÐ°ÑÐ¼ ÑÐ°Ð½Ñ ÑÐµÐ´Ð°ÐºÑÐ¾ÑÑ Ð½Ðµ Ð¸Ð½Ð¸ÑÐ¸Ð¸ÑÐ¾Ð²Ð°ÑÑ Ð¿ÐµÑÐµÑÐ¸ÑÐ¾Ð²ÐºÑ Ð²ÑÐµÐ¹ ÑÑÑÐ¾ÐºÐ¸
            if (this._wasEditor &&
                this._wasEditor.fieldName === cell.fieldName &&
                this._wasEditor.row === this.rowData) {
              i++;
              continue;
            }
            return false;
          }
        }
        i++;
      }
      return true;
    }

    setDisabledFields() {
      this.cells.forEach(cell => {
        cell.setDisabled(
          this.state.dragDrop.disabledFields.indexOf(cell.fieldName) >= 0);
      });
    }

    protected setParams(byInit: boolean = false) {
      if (byInit) {
        this.renderRow();
        this._viewInitialized = true;
        return;
      }
      if (!this._viewInitialized) {
        return;
      }
    }

    protected checkViewPort(): boolean {
      return this._left_rendered <= this.viewPortLeft
         && this._right_rendered >= (this.viewPortLeft + this.viewPortWidth);
    }

    protected check(): boolean {

      const localeChanged: boolean = this.locale !== this._locale0;
      let settingsChanged = false;

      if (this._checkedAppearance !== this.sta.enableCheckedAppearance) {
        settingsChanged = true;
      }

      let valuesChanged = false;
      //let editorChanged = false;

      const columnsChanged = !this.checkColumns();
      if (!columnsChanged) {
        valuesChanged = !this.checkValues();
        //if (!valuesChanged) {
          //editorChanged = !this.checkEditor();
        //}
      }
      return !(columnsChanged || valuesChanged || //editorChanged ||
        settingsChanged || localeChanged);
    }

    ngOnChanges(changes: {[property: string]: SimpleChange }) {
      this.setParams();
    }

    ngDoCheck() {

      if (!this._viewInitialized) {
        return;
      }

      if (!this.check()) {
        // ÐÐ·Ð¼ÐµÐ½Ð¸Ð»Ð¸ÑÑ Ð¾ÑÐ½Ð¾Ð²Ð½ÑÐµ Ð¿Ð°ÑÐ°Ð¼ÐµÑÑÑ ÑÑÑÐ¾ÐºÐ¸ (ÐºÐ¾Ð»Ð¾Ð½ÐºÐ¸, Ð·Ð½Ð°ÑÐµÐ½Ð¸Ñ, ÑÐµÐ´Ð°ÐºÑÐ¾Ñ)
        const hasEditor = this.state.ui.editor && this.state.ui.editor.row === this.rowData;
        // ÐÑÐ¸ÑÐ°ÐµÐ¼
        this.clear(hasEditor);
        // Ð Ð·Ð°Ð½Ð¾Ð²Ð¾ ÑÐ¾ÑÐ¼Ð¸ÑÑÐµÐ¼ ÑÑÑÐ¾ÐºÑ
        this.renderRow();
        return;
      }

      //
      if (!this.checkEditor()) {
        this.clearEditor();
        this.renderRowEditor();
      }

      // ÐÐ·Ð¼ÐµÐ½Ð¸Ð»Ð¸ÑÑ Ð¿Ð°ÑÐ°Ð¼ÐµÑÑÑ Ð¾ÑÐ¾Ð±ÑÐ°Ð¶ÐµÐ½Ð¸Ñ
      if (!this.checkViewPort()) {
        // Ð¤Ð¾ÑÐ¼Ð¸ÑÑÐµÐ¼ ÑÐ¾Ð»ÑÐºÐ¾ ÑÐµ ÑÑÐµÐ¹ÐºÐ¸, ÐºÐ¾ÑÐ¾ÑÑÐµ Ð´Ð¾Ð±Ð°Ð²Ð»ÐµÐ½Ñ Ðº ÑÐ¶Ðµ Ð¾ÑÑÐµÐ½Ð´ÐµÑÐµÐ½Ð½ÑÐ¼
        this.renderByViewPort();
      }

      this.setSelection();
      this.setDisabledFields();
    }

    ngAfterContentInit() {
      this.setParams(true);
    }

    ngOnDestroy() {
      this.clear();
    }

    constructor(
      protected _renderer: Renderer2,
      protected _cfResolver: ComponentFactoryResolver,
      protected _appRef: ApplicationRef,
      protected _injector: Injector,
      public elementRef: ElementRef) { }
}
